import { cn, NoddiIcon } from 'noddi-ui-common';
import { Fragment } from 'react';
import { Button } from '../../../../../base/button/button';
import type { FilterControl } from '../../../../../types/filterControl';
import { NoddiBadge } from '../../../../Other/NoddiBadge';
import { NoddiSeparator } from '../../../../Other/NoddiSeparator';
import { resolveTableFilterTranslations, type TableFilterTranslations } from '../../tableTranslationTypes';
import { getAppliedFilterValues } from './appliedFilterValues';

type Props = {
  controls: FilterControl[];
  translations?: Partial<TableFilterTranslations>;
};

/** The filter sidebar's applied values, grouped by filter, each removable on its own. */
export function AppliedFilters({ controls, translations }: Props) {
  const texts = resolveTableFilterTranslations(translations);
  const groups = controls
    .map((control) => ({ control, values: getAppliedFilterValues(control, texts) }))
    .filter(({ values }) => values.length > 0);

  if (groups.length === 0) {
    return <p className='py-2 text-sm text-muted-foreground'>{texts.noFiltersApplied}</p>;
  }

  return (
    <div className='flex flex-col gap-4'>
      {groups.map(({ control, values }, index) => (
        <Fragment key={control.id}>
          <section aria-label={control.title} className='flex flex-col gap-2'>
            <p className='text-sm font-medium'>{control.title}</p>
            <ul className='flex flex-col gap-1'>
              {values.map((value) => (
                <li key={value.key} className='flex min-h-6 items-center gap-2'>
                  <span
                    className={cn(
                      'min-w-0 flex-1 text-sm break-words md:text-base',
                      value.isExcluded && 'text-muted-foreground line-through'
                    )}
                  >
                    {value.label}
                  </span>
                  {value.isExcluded && (
                    <NoddiBadge variant='destructive' className='px-1 py-0 text-[10px]'>
                      {texts.excluded}
                    </NoddiBadge>
                  )}
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    className='size-6 shrink-0 p-0'
                    aria-label={texts.removeValue(control.title, value.label)}
                    onClick={value.remove}
                  >
                    <NoddiIcon name='X' size='small' />
                  </Button>
                </li>
              ))}
            </ul>
          </section>
          {index < groups.length - 1 && <NoddiSeparator />}
        </Fragment>
      ))}
    </div>
  );
}
